import React, { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { GetPayrollPreflight } from '../../../../api/Payroll/monthlyPayroll';
import { GetPayrollTableExportSettings } from '../../../../api/Payroll/payrollReports';
import { Spinner } from '../../../../components/ui/Spinner';
import { useComplianceCountry } from '../../../../hooks/useComplianceCountry';
import PayrollExportForm, { type PayrollExportFormProps } from './PayrollExportForm';

interface Props extends PayrollExportFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownloaded: (fileName: string) => void;
}

type ReadyProps = Omit<React.ComponentProps<typeof PayrollExportForm>, 'access'> & { labourCardApplies: boolean | null };

/**
 * Mounts with the panel, so the company's bank details setting is read on every open and a change since the last
 * one applies. If the read fails the panel still works, and the server still drops the columns when they are off.
 */
const ExportFormWhenReady: React.FC<ReadyProps> = ({ labourCardApplies, ...form }) => {
  const [bankDetailsAllowed, setBankDetailsAllowed] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    GetPayrollTableExportSettings()
      .then((r) => { if (!cancelled) setBankDetailsAllowed(r.payload?.allowBankDetails ?? true); })
      .catch(() => { if (!cancelled) setBankDetailsAllowed(true); });
    return () => { cancelled = true; };
  }, []);

  if (labourCardApplies === null || bankDetailsAllowed === null) {
    return <div className="flex flex-1 items-center justify-center"><Spinner size="md" /></div>;
  }
  return <PayrollExportForm {...form} access={{ labourCardApplies, bankDetailsAllowed }} />;
};

/**
 * CO-995 — the /payroll/table export options, as a side sheet over the page (the same Radix pattern as
 * views/Settings/Subscriptions/CompanyBillingSheet; the repo has no shared sheet component). A sheet
 * rather than a centred dialog: the choices and the employee list need the height.
 */
const PayrollExportPanel: React.FC<Props> = ({ open, onOpenChange, onDownloaded, ...form }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  // The labour card is a UAE WPS field. The company view carries the compliance country but not the
  // country a None tenant sits in, so that case asks the pre-flight, which owns the rule, about one user.
  const complianceCountry = useComplianceCountry();
  const [noneTenantLabourCard, setNoneTenantLabourCard] = useState<boolean | null>(null);
  const probeUserId = form.payslips[0]?.userId;
  useEffect(() => {
    if (!open || complianceCountry !== 'None' || noneTenantLabourCard !== null || !probeUserId) return undefined;
    let cancelled = false;
    GetPayrollPreflight({ year: form.year, month: form.month, userIds: [probeUserId] })
      .then((r) => { if (!cancelled) setNoneTenantLabourCard(!!r.payload?.labourCardRequired); })
      .catch(() => { if (!cancelled) setNoneTenantLabourCard(false); });
    return () => { cancelled = true; };
  }, [open, complianceCountry, noneTenantLabourCard, probeUserId, form.year, form.month]);

  const labourCardApplies = complianceCountry === 'UAE' ? true : complianceCountry === 'KSA' ? false : noneTenantLabourCard;

  const requestOpenChange = (next: boolean) => { if (!busy) onOpenChange(next); };
  // Every way out is locked together while the file is being made.
  const holdWhileBusy = (e: Event) => { if (busy) e.preventDefault(); };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={requestOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          onEscapeKeyDown={holdWhileBusy}
          onPointerDownOutside={holdWhileBusy}
          onInteractOutside={holdWhileBusy}
          className="fixed inset-y-0 end-0 z-50 flex w-full max-w-xl flex-col border-s border-border bg-card shadow-2xl lg:max-w-[60rem]
            data-[state=open]:animate-in data-[state=open]:ltr:slide-in-from-right data-[state=open]:rtl:slide-in-from-left
            data-[state=closed]:animate-out data-[state=closed]:ltr:slide-out-to-right data-[state=closed]:rtl:slide-out-to-left
            data-[state=open]:duration-200 data-[state=closed]:duration-150"
          data-testid="payroll-export-panel"
        >
          <div className="flex items-start justify-between gap-3 border-b border-border p-5">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-lg font-semibold text-foreground">{t('payrollExport.open')}</DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-0.5 text-sm text-muted-foreground">
                {form.currencyCode
                  ? t('payrollExport.subtitle', { period: form.periodText, currency: form.currencyCode })
                  : form.periodText}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              disabled={busy}
              aria-label={t('Close')}
              className="shrink-0 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          <ExportFormWhenReady
            {...form}
            labourCardApplies={labourCardApplies}
            busy={busy}
            setBusy={setBusy}
            onCancel={() => onOpenChange(false)}
            onDownloaded={(fileName) => { onOpenChange(false); onDownloaded(fileName); }}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default PayrollExportPanel;
